import React from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';

import { DialogDescription, DialogTitle } from '@kit/ui/dialog';
import { DialogPrimitiveComponents as Dialog } from '@kit/ui/marketing';
import { cn } from '@kit/ui/utils';

import GradientNoiseGlow from '~/components/survey-builder/shared/gradient-noise-glow';

export default function SolutionCard({
  visibleBackground,
  data,
}: {
  visibleBackground: boolean;
  data: {
    title: string;
    image?: string;
    content: React.ReactNode;
    shadowCover?: boolean;
  };
}) {
  const [open, setOpen] = React.useState(false);
  const cardRef = React.useRef<HTMLDivElement>(null);
  const layoutId = React.useId();

  data.image = data.image ?? '';

  return (
    <>
      <motion.div
        ref={cardRef}
        layoutId={layoutId}
        onClick={() => setOpen(true)}
        className={cn(
          'group relative h-full cursor-pointer',
          open ? 'pointer-events-none' : '',
        )}
        initial={false}
        transition={{
          layout: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
          opacity: { duration: 0.3 },
        }}
      >
        <GradientNoiseGlow
          className={cn(
            'relative h-full overflow-hidden rounded-[11px] bg-[#1F1D1F] transition-colors duration-200 lg:rounded-[20px]',
            'z-[1] grid gap-1.5 p-1.5',
          )}
          borderRadiusClassName={cn('rounded-3xl', {
            '!border-transparent': !visibleBackground,
          })}
          visible={visibleBackground}
          size="large"
        >
          <ImageWrapper
            imageUrl={data.image}
            alt={data.title}
            className="mx-auto w-full rounded-[5px] lg:rounded-[10px]"
            layoutId={layoutId}
            shadowColor="#1F1D1F"
            shadowCover={data.shadowCover}
          />
          <motion.div
            layoutId={`${layoutId}-content`}
            className="flex w-full items-center gap-1 px-4 py-3 lg:gap-2.5 lg:px-6 lg:py-5"
          >
            <motion.h3
              layoutId={`${layoutId}-h3`}
              className="flex-1 text-[10px] font-medium text-white lg:text-xl"
            >
              {data.title}
            </motion.h3>
            <motion.span
              className="rounded-full bg-white/10 p-1 transition-colors duration-300 ease-in-out hover:bg-white/20 lg:p-2"
              animate={{ opacity: open ? 0 : 1 }}
            >
              <ChevronRight className="h-2 w-2 text-white lg:h-4 lg:w-4" />
            </motion.span>
          </motion.div>
        </GradientNoiseGlow>
      </motion.div>

      <AnimatePresence>
        {open && (
          <Dialog.Root open={open} onOpenChange={setOpen}>
            <Dialog.Portal forceMount>
              <Dialog.Overlay>
                <motion.div
                  className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xl"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                />
              </Dialog.Overlay>

              <Dialog.Content className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 transform outline-none">
                <DialogTitle className="hidden"></DialogTitle>

                <DialogDescription className="hidden"></DialogDescription>

                <motion.div
                  layoutId={layoutId}
                  className="scrollbar-hidden relative z-[1] max-h-[90vh] w-[840px] max-w-[calc(100vw-32px)] flex-col overflow-auto rounded-[20px] bg-[#181618]"
                  transition={{
                    layout: {
                      duration: 0.6,
                      ease: [0.22, 1, 0.36, 1],
                    },
                    opacity: {
                      duration: 0.3,
                    },
                  }}
                >
                  <ImageWrapper
                    imageUrl={data.image}
                    alt={data.title}
                    className="mx-auto w-full max-w-[400px]"
                    layoutId={layoutId}
                    shadowColor="#181618"
                    shadowCover={data.shadowCover}
                  />
                  <motion.div
                    layoutId={`${layoutId}-content`}
                    className="p-4 pt-6 lg:p-14"
                  >
                    <motion.h3
                      layoutId={`${layoutId}-h3`}
                      className="text-xl font-semibold text-white lg:text-5xl"
                    >
                      {data.title}
                    </motion.h3>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        delay: 0.3,
                        duration: 0.5,
                        ease: [0.22, 1, 0.36, 1],
                      }}
                      className="mt-4 text-sm lg:mt-9 lg:text-base"
                    >
                      {data.content}
                    </motion.div>
                  </motion.div>
                </motion.div>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
        )}
      </AnimatePresence>
    </>
  );
}

function ImageWrapper({
  imageUrl,
  alt = '',
  className,
  layoutId,
  shadowColor,
  shadowCover,
}: {
  imageUrl: string;
  alt?: string;
  className?: string;
  layoutId: string;
  shadowColor?: string;
  shadowCover?: boolean;
}) {
  return (
    <motion.div
      layoutId={`${layoutId}-image`}
      className={cn('relative z-[-1]', className)}
      style={{ '--gradient-shadow-color': shadowColor } as React.CSSProperties}
    >
      <img src={imageUrl} alt={alt} className="h-full w-full object-cover" />
      <div className="absolute inset-y-0 -left-1.5 w-18 bg-gradient-to-r from-[var(--gradient-shadow-color)] to-transparent" />
      <div className="absolute inset-y-0 -right-1.5 w-18 bg-gradient-to-l from-[var(--gradient-shadow-color)] to-transparent" />
      {shadowCover && (
        <div className="absolute inset-x-0 bottom-0 h-[396px] bg-gradient-to-t from-[var(--gradient-shadow-color)] to-transparent" />
      )}
    </motion.div>
  );
}
